import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Decimal } from 'decimal.js';
import * as moment from 'moment';
import { merge, Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { OrganizationService } from '../../organizations/organization.service';
import { Organization } from '../../shared/sdk';
import { LanguageService } from '../../shared/services/language.service';
import { MyDpDateService } from '../../shared/services/my-dp-date.service';
import { Guestbook } from '../guestbook';
import { GuestbookService } from '../guestbook.service';
import { Property } from '../property';

Decimal.set({ precision: 5, rounding: 2 });

@Component({
  selector: 'app-guestbook-add',
  templateUrl: './guestbook-add.component.html',
  styleUrls: ['./guestbook-add.component.css'],
})
export class GuestbookAddComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() activeOrg: Organization;
  @Input() guestbook: Guestbook;
  @Input() properties: Property[];
  @Input() duplicate: boolean;
  @ViewChild('firstName') firstName: ElementRef;

  companySearchDataSource: Observable<any>;
  countries: any[];
  currencyId: string;
  documentTypes: any[];
  editing: boolean;
  guestbookForm: FormGroup;
  language: string;
  loading = false;
  promoTaxValue: number;
  promoTaxHalfValue: number;
  promoTaxTotal = 0;
  property: Property;
  sent: boolean;
  taxTypes: any[];
  total = 0;
  ttaxHalfValue: number;
  ttaxTotal = 0;
  ttaxValue: number;
  valueTimes = 1;
  valueType = '';
  private countrySearchFocusObs$: Subject<null> = new Subject();
  readonly countrySearchFocusObs: Observable<Organization> =
    this.countrySearchFocusObs$.asObservable();
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private guestbookService: GuestbookService,
    private orgService: OrganizationService,
    private translate: TranslateService,
    private fb: FormBuilder,
    private languageService: LanguageService,
    private myDpDateService: MyDpDateService,
  ) {
    this.documentTypes = this.guestbookService.documentTypes;
    this.taxTypes = this.guestbookService.taxTypes;
  }

  ngOnInit(): void {
    // lg
    moment.locale(this.activeOrg.locale);
    this.countries = this.guestbookService.countriesA;

    this.languageService
      .getCurrent()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((l) => {
        this.language = l;
      });

    // Form control
    this.guestbookForm = this.fb.group({
      // reservationId: [''],
      propertyId: [this.properties[0].id, Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      gender: ['', Validators.required],
      birthDate: [
        this.myDpDateService.generate(
          moment().subtract(18, 'years').subtract(1, 'day'),
        ),
      ],
      country: ['', Validators.required],
      countrySearch: [''],
      documentType: ['P', Validators.required],
      documentNumber: ['', Validators.required],
      checkintime: [this.myDpDateService.generate(moment())],
      checkintimeTime: [moment().format('LT'), Validators.required],
      checkouttime: [this.myDpDateService.generate(moment().add(1, 'day'))],
      checkouttimeTime: [
        moment().add(1, 'day').format('LT'),
        Validators.required,
      ],
      nights: [1],
      ttaxType: [this.taxTypes[0].code, Validators.required],
      // checkTimesDontUpdateWithReservation: ['', Validators.required],
      id: [],
    });

    this.currencyId = this.orgService.findDefault(
      this.activeOrg,
      'currencyId',
    ).value;

    this.setTaxValues();
    this.calculateTotals();

    this.guestbookForm.controls.ttaxType.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.onChangeTTaxType());

    this.guestbookForm.controls.propertyId.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((propertyId) => {
        this.setTaxValues();
        this.calculateTotals();
      });

    this.guestbookForm.controls.checkintime.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.onChangeCheckin());
    this.guestbookForm.controls.checkouttime.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.onChangeCheckout());

    this.guestbookForm.controls.birthDate.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.onChangeBirthDate());

    if (!this.guestbook.id && !this.duplicate) {
      this.guestbook = new Guestbook();
    } else {
      let guestbook: any = this.guestbook;

      // CIT
      const checkintime = guestbook.checkintime;
      guestbook.checkintime = this.myDpDateService.generate(
        moment.utc(checkintime),
      );
      guestbook.checkintimeTime = moment.utc(checkintime).format('LT');

      // COT
      const checkouttime = guestbook.checkouttime;
      guestbook.checkouttime = this.myDpDateService.generate(
        moment.utc(checkouttime),
      );
      guestbook.checkouttimeTime = moment.utc(checkouttime).format('LT');

      // DOB
      guestbook.birthDate = this.myDpDateService.generate(
        moment.utc(guestbook.birthDate),
      );

      // Country search string
      const countryCode = guestbook.country;

      if (countryCode) {
        this.guestbookForm.controls.countrySearch.patchValue(
          {
            id: countryCode,
            name: this.guestbookService.countries[countryCode],
          },
          { emit: false },
        );
      }

      guestbook = new Guestbook(guestbook);

      this.guestbookForm.patchValue(guestbook);
    }

    this.guestbookForm.controls.countrySearch.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((c) => {
        this.guestbookForm.controls.country.patchValue(c ? c.id : '');
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.firstName.nativeElement.focus(), 100);
  }

  get minBirthDate(): NgbDateStruct {
    return this.myDpDateService.generate(moment().add(-99, 'years'));
  }

  searchCountries = (text$: Observable<string>) => {
    const defaults = [
      { name: 'Slovenija', id: 'SI' },
      { name: 'Italija', id: 'IT' },
      { name: 'Belgija', id: 'BE' },
      { name: 'Grčija', id: 'GR' },
      { name: 'Nemčija', id: 'DE' },
    ];

    return merge(text$, this.countrySearchFocusObs).pipe(
      debounceTime(200),
      map((term: string | null) => {
        if (typeof term === 'string') {
          return term === ''
            ? defaults
            : this.countries
                .filter(
                  (c) => c.name.toLowerCase().indexOf(term.toLowerCase()) > -1,
                )
                .slice(0, 10);
        } else {
          return defaults;
        }
      }),
    );
  };

  onFocusCountry(): void {
    this.countrySearchFocusObs$.next(null);
  }

  formatter = (x: { name: string }) => x.name;

  calculateTotals(): void {
    let total = new Decimal(0);

    const ttaxTotal = new Decimal(this[`ttax${this.valueType}Value`] || 0)
      .times(this.guestbookForm.controls.nights.value)
      .times(this.valueTimes)
      .toDP(2, Decimal.ROUND_HALF_UP);

    const promoTaxTotal = new Decimal(
      this[`promoTax${this.valueType}Value`] || 0,
    )
      .times(this.guestbookForm.controls.nights.value)
      .times(this.valueTimes)
      .toDP(2, Decimal.ROUND_HALF_UP);

    total = total.plus(ttaxTotal).plus(promoTaxTotal);

    this.ttaxTotal = ttaxTotal.toNumber();
    this.promoTaxTotal = promoTaxTotal.toNumber();
    this.total = total.toNumber();
  }

  onChangeBirthDate(): void {
    const checkinCtrl = this.guestbookForm.controls.checkintime;
    const checkintime = moment(this.myDpDateService.parse(checkinCtrl.value));
    const birthDateCtrl = this.guestbookForm.controls.birthDate;
    const birthDate = moment(this.myDpDateService.parse(birthDateCtrl.value));

    const minus7 = checkintime.clone().add(-7, 'years');
    const minus18 = checkintime.clone().add(-18, 'years');

    if (birthDate.isSameOrAfter(minus7, 'day')) {
      this.guestbookForm.controls.ttaxType.setValue('1');
    } else if (birthDate.isSameOrAfter(minus18, 'day')) {
      this.guestbookForm.controls.ttaxType.setValue('11');
    } else {
      this.guestbookForm.controls.ttaxType.setValue('0');
    }
  }

  onChangeCheckin(): void {
    const checkinCtrl = this.guestbookForm.controls.checkintime;
    const checkintime = moment(this.myDpDateService.parse(checkinCtrl.value));
    const checkoutCtrl = this.guestbookForm.controls.checkouttime;
    let checkouttime = moment(this.myDpDateService.parse(checkoutCtrl.value));

    if (checkintime.isAfter(checkouttime)) {
      checkoutCtrl.patchValue(this.myDpDateService.generate(checkintime), {
        emit: false,
      });
      checkouttime = checkintime;
    }

    const diff = checkouttime.diff(checkintime, 'days');
    this.setNights(diff);
  }

  onChangeCheckout(): void {
    const checkinCtrl = this.guestbookForm.controls.checkintime;
    let checkintime = moment(this.myDpDateService.parse(checkinCtrl.value));
    const checkoutCtrl = this.guestbookForm.controls.checkouttime;
    const checkouttime = moment(this.myDpDateService.parse(checkoutCtrl.value));

    if (checkouttime.isBefore(checkintime)) {
      checkinCtrl.patchValue(this.myDpDateService.generate(checkouttime), {
        emit: false,
      });
      checkintime = checkouttime;
    }

    const diff = checkouttime.diff(checkintime, 'days');
    this.setNights(diff);
  }

  onChangeNights(n: number): void {
    const nights = this.guestbookForm.controls.nights.value + n;
    const checkoutCtrl = this.guestbookForm.controls.checkouttime;
    const checkinCtrl = this.guestbookForm.controls.checkintime;

    checkoutCtrl.patchValue(
      this.myDpDateService.generate(
        moment(this.myDpDateService.parse(checkinCtrl.value)).add(
          nights,
          'days',
        ),
      ),
    );

    this.setNights(nights);
  }

  onChangeTTaxType(): void {
    const ttaxCtrl = this.guestbookForm.controls.ttaxType;
    const ttaxType = this.taxTypes.find((tt) => tt.code === ttaxCtrl.value);

    switch (ttaxType.proc) {
      case '1':
        this.valueType = '';
        this.valueTimes = 1;
        break;

      case '0.5':
        this.valueType = 'Half';
        this.valueTimes = 1;
        break;

      case '0':
        this.valueType = '';
        this.valueTimes = 0;
        break;
    }

    this.calculateTotals();
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onDelete(): void {
    if (confirm(this.translate.instant('Really delete?'))) {
      this.activeModal.close({ delete: this.guestbook.id });
    }
  }

  onDuplicate(): void {
    this.activeModal.close({ duplicate: this.guestbook });
  }

  onSave(): void {
    this.sent = true;

    if (this.guestbookForm.valid) {
      this.loading = true;
      const guestbook = this.guestbookForm.value;
      this.editing = !!this.guestbook.id;

      // CIT
      let checkintime = guestbook.checkintime;
      const checkintimeTime = guestbook.checkintimeTime.split(':');
      checkintime = moment
        .utc(this.myDpDateService.parse(checkintime))
        .set({ hour: checkintimeTime[0], minute: checkintimeTime[1] })
        .format();
      guestbook.checkintime = checkintime;

      // COT
      let checkouttime = guestbook.checkouttime;
      const checkouttimeTime = guestbook.checkintimeTime.split(':');
      checkouttime = moment
        .utc(this.myDpDateService.parse(checkouttime))
        .set({ hour: checkouttimeTime[0], minute: checkouttimeTime[1] })
        .format();
      guestbook.checkouttime = checkouttime;

      // DOB
      guestbook.birthDate = moment
        .utc(this.myDpDateService.parse(guestbook.birthDate))
        .format();

      // Create
      if (!this.editing) {
        this.guestbookService.create(guestbook).subscribe(
          (created) => {
            this.activeModal.close(created);
            this.loading = false;
            this.guestbook = guestbook;
          },
          () => (this.loading = false),
        );
      } else {
        // Update
        guestbook.status = 1;

        this.guestbookService.update(guestbook.id, guestbook).subscribe(
          (updated) => {
            this.activeModal.close(updated);
            this.loading = false;
            this.guestbook = guestbook;
          },
          () => (this.loading = false),
        );
      }
    }
  }

  // onSelectCountry($event: NgbTypeaheadSelectItemEvent) {
  // }

  private setNights(nights: number): void {
    const nightsCtrl = this.guestbookForm.controls.nights;
    nightsCtrl.patchValue(nights);

    this.calculateTotals();
  }

  private setTaxValues(): void {
    const prop = this.properties.find(
      (p) => p.id === this.guestbookForm.controls.propertyId.value,
    );
    const checkinDate = moment(this.guestbookForm.controls.checkintime.value);
    let ttaxValues;

    this.property = prop;

    for (let i = prop.ttaxValues.length - 1; i >= 0; i--) {
      const ttv = prop.ttaxValues[i];

      if (moment(ttv.fromDate).isSameOrBefore(checkinDate)) {
        ttaxValues = ttv;
        break;
      }
    }

    // Date before first tax value option
    if (!ttaxValues) {
      ttaxValues = {
        value: 0,
        halfValue: 0,
        promoTaxValue: 0,
        promoTaxHalfValue: 0,
      };
    }

    this.ttaxValue = ttaxValues.value;
    this.ttaxHalfValue = ttaxValues.halfValue;
    this.promoTaxValue = ttaxValues.promoTaxValue;
    this.promoTaxHalfValue = ttaxValues.promoTaxHalfValue;
  }
}
